import { createApiAxiosInstance } from '../utils/axiosConfig';

// Use centralized axios configuration with standardized interceptors
const individualsAxios = createApiAxiosInstance();

export interface IndividualAddressDto {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
    complement?: string;
}

export interface IndividualDto {
    id: string;
    name: string;
    cpf: string;
    phone: string;
    birthDate: string;
    rg?: string;
    email?: string;
    professionalEntity?: string;
    address?: IndividualAddressDto;
    registrationPhase: number; // 1=Personal, 2=Documents, 3=Services, 4=Completed
    isActive: boolean;
    notes?: string;
    createdAt: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
    createdByName?: string;
    updatedByName?: string;
    hasServices?: boolean;     // True if individual has any services
}

export interface CreateIndividualRequest {
    name: string;
    cpf: string;
    phone: string;
    birthDate: string;
    senhaGov?: string;
    rg?: string;
    email?: string;
    professionalEntity?: string;
    address?: IndividualAddressDto;
}

export interface UpdateIndividualRequest extends CreateIndividualRequest {
    individualId: string;
}

export interface CreateIndividualResult {
    individualId: string;
    message: string;
}

export interface UpdateIndividualResult {
    message: string;
}

export interface CompleteIndividualPhaseResult {
    message: string;
    currentPhase: number;
}

export interface ServiceSelectionDto {
    serviceTypeId: string;
    isRecurring: boolean;
    recurrenceInterval?: string; // MONTHLY, QUARTERLY, YEARLY
    customPrice?: number;
    notes?: string;
}

export interface AddIndividualServicesRequest {
    individualId: string;
    services: ServiceSelectionDto[];
}

export interface AddIndividualServicesResult {
    individualId: string;
    servicesAdded: number;
    serviceIds: string[];
}

export interface IndividualServiceDto {
    id: string;
    serviceTypeId: string;
    status: string;
    startDate: string;
    endDate?: string;
    customPrice?: number;
    notes?: string;
    isRecurring: boolean;
    recurrenceInterval?: string;
    nextDueDate?: string;
}

export interface GetIndividualServicesResult {
    individualId: string;
    services: IndividualServiceDto[];
}

export const individualsService = {
    /**
     * Get all individuals
     */
    getAll: async (): Promise<IndividualDto[]> => {
        return individualsAxios.get('/individuals');
    },

    /**
     * Get individual by ID
     */
    getById: async (id: string): Promise<IndividualDto> => {
        return individualsAxios.get(`/individuals/${id}`);
    },

    /**
     * Create a new individual (Phase 1: Personal Information)
     */
    create: async (individual: CreateIndividualRequest): Promise<CreateIndividualResult> => {
        return individualsAxios.post('/individuals', individual);
    },

    /**
     * Update an existing individual
     */
    update: async (individual: UpdateIndividualRequest): Promise<UpdateIndividualResult> => {
        return individualsAxios.put(`/individuals/${individual.individualId}`, individual);
    },

    /**
     * Delete an individual (soft delete)
     */
    delete: async (id: string): Promise<void> => {
        await individualsAxios.delete(`/individuals/${id}`);
    },

    // ===== PHASE COMPLETION ENDPOINTS =====

    /**
     * Complete Phase 1 (Personal Information) and advance to Phase 2 (Documents)
     */
    completePhase1: async (id: string): Promise<CompleteIndividualPhaseResult> => {
        return individualsAxios.post(`/individuals/${id}/complete-phase1`, {});
    },

    /**
     * Complete Phase 2 (Documents) and advance to Phase 3 (Services)
     */
    completePhase2: async (id: string): Promise<CompleteIndividualPhaseResult> => {
        return individualsAxios.post(`/individuals/${id}/complete-phase2`, {});
    },

    /**
     * Complete Phase 3 (Services) and mark registration as complete
     */
    completePhase3: async (id: string, hasServices: boolean): Promise<CompleteIndividualPhaseResult> => {
        return individualsAxios.post(`/individuals/${id}/complete-phase3?hasServices=${hasServices}`, {});
    },

    /**
     * Add services to an individual
     */
    addServices: async (request: AddIndividualServicesRequest): Promise<AddIndividualServicesResult> => {
        return individualsAxios.post(`/individuals/${request.individualId}/services`, request);
    },

    /**
     * Get services for an individual
     */
    getServices: async (individualId: string): Promise<GetIndividualServicesResult> => {
        return individualsAxios.get(`/individuals/${individualId}/services`);
    },

    /**
     * Validate CPF
     */
    validateCpf: async (cpf: string): Promise<{ isValid: boolean; formattedCpf?: string; errorMessage?: string }> => {
        return individualsAxios.post('/individuals/validate-cpf', { cpf });
    },
};
